import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { RefreshCw, Download, ImageOff, Eye, EyeOff, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductsWithoutImagePanel = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [hideHidden, setHideHidden] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/products/without-image`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const products = (data?.products || []).filter((p) => {
    if (hideHidden && p.hidden) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (p.ref || '').toLowerCase().includes(s) || (p.name || '').toLowerCase().includes(s);
  });

  const downloadCsv = () => {
    if (!data?.products?.length) return;
    const rows = [
      ['REF', 'NOM', 'CACHE', 'PRIX_JOUR', 'PRIX_VENTE', 'ID_BO'],
      ...data.products.map((p) => [
        p.ref || '', (p.name || '').replace(/[\r\n]+/g, ' '),
        p.hidden ? 'OUI' : 'NON',
        p.daily_price ?? '', p.sale_price ?? '', p.id ?? '',
      ]),
    ];
    const csv = rows.map((r) =>
      r.map((c) => {
        const v = String(c);
        return /[;"\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
      }).join(';')
    ).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `produits-sans-photo-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card className="rounded-none">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <ImageOff className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
            <div>
              <CardTitle className="text-base">Produits sans photo</CardTitle>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                Produits du Backoffice qui n&apos;ont aucune image associée (ni dans le BO, ni
                via un override local). Ces produits s&apos;affichent sans visuel côté
                catalogue et nuisent à la conversion. Uploader une image dans le BO ou
                créer un override la fera disparaître de cette liste.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="rounded-none" data-testid="refresh-without-image-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Rafraîchir</span>
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data?.products?.length} className="rounded-none" data-testid="export-without-image-csv-btn">
              <Download className="w-4 h-4" />
              <span className="ml-2 hidden sm:inline">Exporter CSV</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-4">{error}</div>}

        {data && (
          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
            <div>
              <span className="font-semibold">{data.without_image_count}</span>
              <span className="text-muted-foreground"> produits sans photo</span>
              <span className="text-muted-foreground"> / {data.total_in_backoffice} au total</span>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Input type="text" placeholder="Rechercher par REF ou NOM…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none h-8 text-sm" data-testid="without-image-search-input" />
            </div>
            <button type="button" onClick={() => setHideHidden((v) => !v)} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary" data-testid="toggle-hidden-without-image-btn">
              {hideHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {hideHidden ? 'Cachés masqués' : 'Cachés affichés'}
            </button>
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        )}

        {data && (
          <div className="border overflow-x-auto" data-testid="without-image-products-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold w-32">REF</th>
                  <th className="px-3 py-2 font-semibold">NOM</th>
                  <th className="px-3 py-2 font-semibold w-20 text-center">CACHÉ</th>
                  <th className="px-3 py-2 font-semibold w-24 text-right">PRIX/J</th>
                  <th className="px-3 py-2 font-semibold w-24 text-right">VENTE</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-8">
                    {data.without_image_count === 0 ? 'Tous les produits ont une photo. 🎉' : 'Aucun produit ne correspond aux filtres.'}
                  </td></tr>
                )}
                {products.map((p, i) => (
                  <tr key={`${p.id}-${i}`} className={`border-t ${p.hidden ? 'opacity-60' : ''}`} data-testid="without-image-product-row">
                    <td className="px-3 py-2 font-mono text-xs">{p.ref || '—'}</td>
                    <td className="px-3 py-2">{p.name || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {p.hidden
                        ? <span className="text-xs bg-gray-200 px-2 py-0.5">oui</span>
                        : <span className="text-xs text-muted-foreground">non</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.daily_price != null ? `${p.daily_price} €` : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.sale_price != null ? `${p.sale_price} €` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductsWithoutImagePanel;
